import React, { useEffect, useState } from 'react';
import spotlightImage from '../../../assets/images/contact_us/map_img.jpg';
import { GoogleMap, LoadScript, OverlayView } from '@react-google-maps/api';
import { getLocationCoordinates, isReactSnap } from '../../../utils';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { googleMapStyles } from '../../../data/googleMap';

const CLINIC_ADDRESS = '1174 N Leatherleaf Loop Unit D Wasilla, AK 99654';
const DEFAULT_MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  CLINIC_ADDRESS
)}`;

const Map = () => {
  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [coordinates, setCoordinates] = useState(null);
  const [googleMapsUrl, setGoogleMapsUrl] = useState(DEFAULT_MAPS_URL);
  const canLoadInteractiveMap = Boolean(GOOGLE_API_KEY) && !isReactSnap();

  useEffect(() => {
    if (!canLoadInteractiveMap) {
      return;
    }

    const fetchCoordinates = async () => {
      const result = await getLocationCoordinates(CLINIC_ADDRESS);
      if (result) {
        setCoordinates(result);
        setGoogleMapsUrl(
          `https://www.google.com/maps?q=${result.lat},${result.lng}`
        );
      }
    };

    fetchCoordinates();
  }, [canLoadInteractiveMap]);

  const renderMapPreview = () => {
    if (canLoadInteractiveMap && coordinates) {
      return (
        <LoadScript googleMapsApiKey={GOOGLE_API_KEY}>
          <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
            <GoogleMap
              center={coordinates}
              zoom={12.8}
              mapContainerClassName="w-[90vw] h-[170px] sm:w-[510px] sm:h-[220px] mb-2 cursor-pointer"
              options={{
                styles: googleMapStyles,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
              }}
            >
              <OverlayView
                position={coordinates}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <svg width="50" height="50" viewBox="0 0 24 24">
                  <defs>
                    <linearGradient
                      id="gradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="0%"
                    >
                      <stop offset="0%" stopColor="#C672A7" />
                      <stop offset="100%" stopColor="#7F76AF" />
                    </linearGradient>
                  </defs>

                  <FaMapMarkerAlt style={{ fill: 'url(#gradient)' }} />
                </svg>
              </OverlayView>
            </GoogleMap>
          </a>
        </LoadScript>
      );
    }

    return (
      <a
        href={googleMapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="w-[90vw] h-[170px] sm:w-[510px] sm:h-[220px] mb-2 cursor-pointer flex items-center justify-center border border-white/40 rounded-2xl bg-white/10 backdrop-blur"
      >
        <p className="text-sm sm:text-base text-white font-semibold">
          View our Wasilla clinic on Google Maps
        </p>
      </a>
    );
  };

  return (
    <div className="relative w-full h-[430px] xl:h-[600px] overflow-hidden">
      <div className="absolute inset-0 h-full w-full">
        <div
          className="parallax-image h-[150%] w-full bg-cover bg-center"
          style={{
            backgroundImage: `url(${spotlightImage})`,
            backgroundAttachment: 'fixed',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      </div>

      {/* Fixed content */}
      <div className="absolute inset-0 flex justify-center items-center text-white z-10 px-4">
        <div className="flex flex-col justify-center items-center gap-2 sm:gap-[7px] lg:gap-[18px] 3xl:gap-5 tracking-wide text-center">
          <div>{renderMapPreview()}</div>
          <h2 className="text-2xl md:text-5xl 3xl:text-6xl font-bold">
            Alaska's Choice{' '}
            <span className="text-lg md:text-3xl 3xl:text-4xl font-light">
              for Pediatric PT/OT Care
            </span>
          </h2>
          <h3 className="md:text-xl lg:text-2xl 3xl:text-3xl font-semibold">
            Got Questions? We would love to hear from you!
          </h3>
          <h4 className="text-3xl md:text-[44px] 3xl:text-5xl font-[500] font-[Anton] tracking-wider cursor-pointer">
            <a href="tel:+19073764880">907-376-4880</a>
          </h4>
        </div>
      </div>
    </div>
  );
};

export default Map;
